// Public "Report an issue" page — the browser fallback when someone
// scans a location-QR sticker without the mobile app installed.
//
// URL shape:
//   /report/:orgSlug/:siteId              → site-level QR
//   /report/:orgSlug/:siteId/:locationId  → location-level QR
//
// If the visitor is signed into the SPA (they're already an internal
// user), we redirect to the internal location detail rather than
// showing the public form — they don't need to re-enter their name.
//
// Behaviour when no issue templates are configured for the site:
//   The "What's the problem?" field falls back to a plain text input
//   so the QR is usable even before an admin sets up templates.
//
// Visual language matches the internal "Report an issue" dialog on
// the Requests page so the two intake surfaces feel like siblings.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert, Box, Button, CircularProgress, Divider, IconButton, MenuItem, Paper,
  Select, Stack, TextField, Typography,
} from '@mui/material'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiError, type PublicQrBootPayload } from '@cmms/api-client'

import { api } from '../../lib/api'
import { APP_NAME, APP_LOGO } from '../../lib/branding'

type Priority = 'low' | 'medium' | 'high' | 'critical'

const PRIORITY_META: Record<Priority, { label: string; color: string; bg: string }> = {
  low:      { label: 'Low',      color: '#059669', bg: '#ECFDF5' },
  medium:   { label: 'Medium',   color: '#D97706', bg: '#FFFBEB' },
  high:     { label: 'High',     color: '#EA580C', bg: '#FFF7ED' },
  critical: { label: 'Critical', color: '#DC2626', bg: '#FEF2F2' },
}

export function PublicReportIssuePage() {
  const { orgSlug = '', siteId = '', locationId } = useParams<{
    orgSlug: string
    siteId: string
    locationId?: string
  }>()
  const navigate = useNavigate()

  // The public form is shown to *everyone* who lands on /report/... —
  // including signed-in users. The previous auto-redirect to
  // /locations/<id> bounced anyone with a stale token in localStorage
  // to /login (protected route + expired session = login). Mobile
  // deep-links to the internal location screen will land through the
  // native app URL scheme once Universal / App Links ship, not through
  // this SPA route.

  const bootQ = useQuery<PublicQrBootPayload>({
    queryKey: ['public-location-qr', orgSlug, siteId, locationId],
    queryFn: () => api.publicLocationQr.boot(orgSlug, siteId, locationId),
    retry: false,
  })

  const boot = bootQ.data

  if (bootQ.isLoading) {
    return (
      <PublicShell>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      </PublicShell>
    )
  }
  if (bootQ.isError || !boot) {
    return (
      <PublicShell>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {(bootQ.error instanceof ApiError && bootQ.error.message) ||
            "We couldn't load this location. Please double-check the QR code."}
        </Alert>
      </PublicShell>
    )
  }

  return (
    <PublicShell>
      <ReportForm boot={boot} onDone={() => navigate('.', { replace: true })} />
    </PublicShell>
  )
}

// ─── Shell ────────────────────────────────────────────────────────

function PublicShell({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{
      minHeight: '100dvh',
      bgcolor: (t) => t.palette.mode === 'light' ? '#F6F8FA' : 'background.default',
      display: 'flex', flexDirection: 'column',
    }}>
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box component="img" src={APP_LOGO} alt={`${APP_NAME} logo`}
            sx={{ height: 24, width: 'auto' }} />
          <Typography variant="body1" sx={{ fontWeight: 700 }}>{APP_NAME}</Typography>
        </Stack>
      </Box>
      <Box sx={{
        flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        px: { xs: 2, sm: 3 }, pb: 4,
      }}>
        <Paper elevation={0} sx={{
          width: '100%', maxWidth: 640, borderRadius: 3,
          border: '1px solid', borderColor: 'divider',
          overflow: 'hidden',
        }}>
          {children}
        </Paper>
      </Box>
    </Box>
  )
}

// ─── Form ─────────────────────────────────────────────────────────

// Per-tile shape for the photo picker. Each tile owns:
//   • `file` — the local File the user picked, for the preview thumb
//   • `previewUrl` — object URL for the thumbnail
//   • `url`  — public URL returned by the upload endpoint, once
//              the upload succeeds. Sent in the final submit body.
//   • `error` — last upload error message, if the tile failed.
type Attachment = {
  id: string
  file: File
  previewUrl: string
  url: string | null
  error: string | null
}

const MAX_ATTACHMENTS = 4
const MAX_BYTES = 5 * 1024 * 1024  // 5 MB — matches the backend cap.
const ALLOWED_MIME = new Set([
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
])

function ReportForm({ boot, onDone }: { boot: PublicQrBootPayload; onDone: () => void }) {
  const [reporterName, setReporterName]   = useState('')
  const [reporterEmail, setReporterEmail] = useState('')
  const [templateId, setTemplateId] = useState<string>('')
  const [freeTitle, setFreeTitle]   = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<Priority>('medium')
  // Attached photos. Tiles are added on file-pick, uploaded in the
  // background, and removed on × click. Freeing preview URLs happens
  // on unmount so the browser doesn't leak object refs.
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [submitted, setSubmitted] = useState<{ id: string; title: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Clean up object URLs when the component unmounts.
  useEffect(() => {
    return () => {
      for (const a of attachments) URL.revokeObjectURL(a.previewUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The public form's "What's the problem?" surface morphs between
  // dropdown and text input based on whether the admin has any active
  // templates for this site. `useTextInput` flips true when the
  // template list is empty.
  const useTextInput = boot.templates.length === 0

  // When the reporter picks a template that has a priority preset,
  // auto-select it so they don't need to think about severity. They
  // can still change it manually before submitting.
  const selectedTemplate = useMemo(
    () => boot.templates.find((t) => t.id === templateId) ?? null,
    [boot.templates, templateId],
  )
  useEffect(() => {
    if (selectedTemplate?.priority) {
      setPriority(selectedTemplate.priority)
    }
  }, [selectedTemplate])

  // Handle file selection from the picker. Filters oversized / wrong-
  // MIME files client-side with a toast-style message before hitting
  // the network, respects the 4-image cap, then uploads each valid
  // file to the companion endpoint in parallel. Each tile updates
  // independently so a slow one doesn't block the fast ones.
  const handleFilesPicked = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setError(null)
    const slotsLeft = MAX_ATTACHMENTS - attachments.length
    if (slotsLeft <= 0) {
      setError(`You can attach up to ${MAX_ATTACHMENTS} photos.`)
      return
    }

    // Build tile objects for accepted files. Rejects surface as an
    // error string but don't block accepted files from uploading.
    const tiles: Attachment[] = []
    const rejects: string[] = []
    Array.from(files).slice(0, slotsLeft).forEach((f) => {
      if (!ALLOWED_MIME.has(f.type)) {
        rejects.push(`${f.name}: only JPEG, PNG, WebP, or HEIC are allowed`)
        return
      }
      if (f.size > MAX_BYTES) {
        rejects.push(`${f.name}: over 5 MB`)
        return
      }
      tiles.push({
        id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        previewUrl: URL.createObjectURL(f),
        url: null,
        error: null,
      })
    })
    if (rejects.length > 0) setError(rejects.join(' · '))
    if (tiles.length === 0) return
    setAttachments((prev) => [...prev, ...tiles])

    // Fire the uploads. Each promise updates its own tile independently.
    for (const tile of tiles) {
      try {
        const { url } = await api.publicLocationQr.uploadImage(
          boot.org.slug,
          boot.site.id,
          tile.file,
          boot.location?.id,
        )
        setAttachments((prev) => prev.map((a) => a.id === tile.id ? { ...a, url } : a))
      } catch (err) {
        setAttachments((prev) => prev.map((a) => a.id === tile.id
          ? { ...a, error: err instanceof ApiError ? err.message : 'Upload failed' }
          : a))
      }
    }
  }

  const removeAttachment = (id: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id)
      if (target) URL.revokeObjectURL(target.previewUrl)
      return prev.filter((a) => a.id !== id)
    })
  }

  const anyUploading = attachments.some((a) => !a.url && !a.error)

  const submitM = useMutation({
    mutationFn: () => api.publicLocationQr.submit(
      boot.org.slug,
      boot.site.id,
      {
        reporter_name:  reporterName.trim(),
        reporter_email: reporterEmail.trim(),
        issue_template_id: useTextInput ? undefined : (templateId || undefined),
        title:             useTextInput ? freeTitle.trim() : undefined,
        description: description.trim() || undefined,
        priority,
        // Only ship the URLs that actually landed on the object store —
        // in-flight tiles are blocked by `anyUploading` in canSubmit,
        // and failed tiles are excluded here as belt-and-suspenders.
        image_urls: attachments
          .map((a) => a.url)
          .filter((u): u is string => typeof u === 'string' && u.length > 0),
      },
      boot.location?.id,
    ),
    onSuccess: (r) => setSubmitted({ id: r.id, title: r.title }),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Couldn't submit"),
  })

  const canSubmit = reporterName.trim()
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reporterEmail.trim())
    && (useTextInput ? freeTitle.trim().length > 0 : templateId.length > 0)
    && !anyUploading
    && !submitM.isPending

  if (submitted) {
    return (
      <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: 99, bgcolor: '#ECFDF5',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          mb: 2,
        }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 36, color: '#059669' }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
          Request submitted
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 3 }}>
          Thanks — we've logged "{submitted.title}" at {boot.site.name}
          {boot.location ? ` (${boot.location.name})` : ''}. The maintenance
          team will review it and email you when it's approved and assigned.
        </Typography>
        <Button variant="outlined" onClick={() => { onDone(); location.reload() }}
          sx={{ borderRadius: 2, fontWeight: 600 }}>
          File another request
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header — matches the internal dialog */}
      <Box sx={{ p: { xs: 3, sm: 3.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.75 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 99, bgcolor: '#D9770614', color: '#D97706',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>Report an issue</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Tell us what needs fixing — our maintenance team will review it.
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ p: { xs: 3, sm: 3.5 } }}>
        <Stack spacing={2.5}>
          {/* Location context — read-only chips */}
          <Box sx={{
            p: 1.75, borderRadius: 2, bgcolor: 'action.hover',
            border: '1px solid', borderColor: 'divider',
          }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LocationOnOutlinedIcon sx={{ fontSize: 18, color: '#3B82F6' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                  {boot.site.name}
                </Typography>
                {boot.location && (
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                    {boot.location.name}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>

          {/* Requester identity */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="Your name" required autoFocus
              value={reporterName} onChange={(e) => setReporterName(e.target.value)}
              size="small" fullWidth
              placeholder="e.g. Priya Sharma"
            />
            <TextField
              label="Email" required type="email"
              value={reporterEmail} onChange={(e) => setReporterEmail(e.target.value)}
              size="small" fullWidth
              placeholder="you@example.com"
              helperText="We'll email you approval + status updates."
            />
          </Stack>

          {/* What's the problem? — dropdown OR text input */}
          {useTextInput ? (
            <TextField
              label="What's the problem?" required
              value={freeTitle} onChange={(e) => setFreeTitle(e.target.value)}
              size="small" fullWidth
              placeholder="e.g. AC not cooling in Room 204"
              helperText="A short summary of the issue"
            />
          ) : (
            <Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
                What's the problem? <span style={{ color: '#d32f2f' }}>*</span>
              </Typography>
              <Select
                fullWidth size="small" value={templateId}
                onChange={(e) => setTemplateId(e.target.value as string)}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  <em style={{ color: '#9CA3AF' }}>Pick an issue…</em>
                </MenuItem>
                {boot.templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    <Stack spacing={0}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                        {t.name}
                      </Typography>
                      {t.description && (
                        <Typography sx={{ fontSize: 11, color: 'text.secondary',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          maxWidth: 460 }}>
                          {t.description}
                        </Typography>
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              {selectedTemplate?.description && (
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.75 }}>
                  {selectedTemplate.description}
                </Typography>
              )}
            </Box>
          )}

          <TextField
            label="More details" multiline minRows={3}
            value={description} onChange={(e) => setDescription(e.target.value)}
            size="small" fullWidth
            placeholder="Describe the issue — when did it start? How urgent is it? Is it affecting work?"
            helperText="Optional. The more detail you provide, the faster we can help."
          />

          {/* Photos — up to 4 images, uploaded in the background as
              they're picked. Each tile spins while uploading; the ×
              button removes a tile locally and (silently) leaves the
              already-uploaded object in the store — cheap trade for
              a clean UX. */}
          <Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
              Photos (optional, up to {MAX_ATTACHMENTS})
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }} useFlexGap>
              {attachments.map((a) => (
                <Box key={a.id} sx={{
                  position: 'relative', width: 88, height: 88, borderRadius: 2,
                  overflow: 'hidden', border: '1px solid',
                  borderColor: a.error ? '#DC2626' : 'divider',
                }}>
                  <Box component="img" src={a.previewUrl} alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  {/* Uploading overlay — grey scrim + centered spinner. */}
                  {!a.url && !a.error && (
                    <Box sx={{
                      position: 'absolute', inset: 0, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,0.35)',
                    }}>
                      <CircularProgress size={22} sx={{ color: '#fff' }} />
                    </Box>
                  )}
                  {/* Failed overlay — red tint + tooltip. */}
                  {a.error && (
                    <Box sx={{
                      position: 'absolute', inset: 0, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      bgcolor: 'rgba(220,38,38,0.35)',
                      color: '#fff', fontSize: 10, fontWeight: 700,
                      textAlign: 'center', px: 0.5, lineHeight: 1.2,
                    }}
                    title={a.error}>
                      Upload failed
                    </Box>
                  )}
                  {/* Remove button. */}
                  <IconButton size="small"
                    onClick={() => removeAttachment(a.id)}
                    sx={{
                      position: 'absolute', top: 2, right: 2,
                      width: 20, height: 20, bgcolor: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                    }}>
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Box>
              ))}
              {attachments.length < MAX_ATTACHMENTS && (
                <Box component="label" htmlFor="public-photo-picker"
                  sx={{
                    width: 88, height: 88, borderRadius: 2,
                    border: '1.5px dashed', borderColor: 'divider',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: 'text.secondary',
                    transition: 'all 120ms ease',
                    '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                  }}>
                  <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 22 }} />
                  <Typography sx={{ fontSize: 10, fontWeight: 700, mt: 0.5 }}>
                    Add photo
                  </Typography>
                </Box>
              )}
            </Stack>
            <input
              id="public-photo-picker"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                handleFilesPicked(e.target.files)
                // Clear the input so picking the same file again
                // re-fires the change event.
                e.target.value = ''
              }}
            />
            <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.75 }}>
              JPEG, PNG, WebP or HEIC · max 5 MB per photo.
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
              Priority
            </Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
              How urgent is this?
            </Typography>
            <Stack direction="row" spacing={1}>
              {(['low', 'medium', 'high', 'critical'] as const).map((p) => {
                const m = PRIORITY_META[p]
                const isSelected = priority === p
                return (
                  <Box key={p} onClick={() => setPriority(p)}
                    sx={{
                      flex: 1, py: 1.25, textAlign: 'center', borderRadius: 2, cursor: 'pointer',
                      border: '1.5px solid',
                      borderColor: isSelected ? m.color : 'divider',
                      bgcolor: isSelected ? m.bg : 'transparent',
                      transition: 'all 120ms', '&:hover': { borderColor: m.color },
                    }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: m.color, mx: 'auto', mb: 0.5 }} />
                    <Typography sx={{ fontSize: 12, fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? m.color : 'text.secondary', textTransform: 'capitalize' }}>
                      {p}
                    </Typography>
                  </Box>
                )
              })}
            </Stack>
          </Box>

          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
          )}
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ px: { xs: 3, sm: 3.5 }, py: 2.5 }}>
        <Stack direction="row" justifyContent="flex-end">
          <Button variant="contained" size="large"
            disabled={!canSubmit}
            onClick={() => { setError(null); submitM.mutate() }}
            sx={{ borderRadius: 2, fontWeight: 700, px: 4 }}>
            {submitM.isPending ? 'Submitting…' : 'Submit request'}
          </Button>
        </Stack>
      </Box>
    </Box>
  )
}
